import React from 'react';
import { Card, Progress, Row, Col, Statistic } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';

interface SystemHealthCardProps {
  healthPercentage: number;
  activeRPCs: number;
  totalRPCs: number;
  loading?: boolean;
}

const SystemHealthCard: React.FC<SystemHealthCardProps> = ({
  healthPercentage,
  activeRPCs,
  totalRPCs,
  loading = false
}) => {
  const { t: dashboardT } = useTranslationWithNamespace('dashboard');

  // Determine status color based on health percentage
  const getStatusColor = (percentage: number) => {
    if (percentage >= 80) return '#52c41a'; // Green
    if (percentage >= 50) return '#faad14'; // Yellow
    return '#f5222d'; // Red
  };

  // Determine status text based on health percentage
  const getStatusText = (percentage: number) => {
    if (percentage >= 80) return dashboardT('systemHealth.healthy', 'Healthy');
    if (percentage >= 50) return dashboardT('systemHealth.partiallyHealthy', 'Partially Healthy');
    return dashboardT('systemHealth.unhealthy', 'Unhealthy');
  };

  // Determine status icon based on health percentage
  const getStatusIcon = (percentage: number) => {
    if (percentage >= 80) return <CheckCircleOutlined />;
    if (percentage >= 50) return <WarningOutlined />;
    return <CloseCircleOutlined />;
  };

  return (
    <Card title={dashboardT('systemHealth.title', 'System Health')} loading={loading}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Progress
            percent={Math.round(healthPercentage)}
            status={
              healthPercentage >= 80 ? 'success' :
              healthPercentage >= 50 ? 'normal' : 'exception'
            }
          />
        </Col>
        <Col span={12}>
          <Statistic
            title={dashboardT('systemHealth.status', 'Status')}
            value={getStatusText(healthPercentage)}
            valueStyle={{ color: getStatusColor(healthPercentage) }}
            prefix={getStatusIcon(healthPercentage)}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title={dashboardT('rpcStatus.activeRpcs', 'Active RPCs')}
            value={`${activeRPCs}/${totalRPCs}`}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default SystemHealthCard;
